import { type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Activity, CreditCard, Megaphone } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute';
import { useAuth } from '@/lib/auth/useAuth';
import { useClientProductAccess } from '@/features/client-portal/useClientProductAccess';
import { LockedProductExperience } from '@/features/client-portal/LockedProductExperience';
import { LoginPage } from '@/features/auth/LoginPage';
import { UnauthorizedPage } from '@/features/auth/UnauthorizedPage';
import { OverviewPage } from '@/features/overview/OverviewPage';
import { UsageAnalyticsPage } from '@/features/analytics/UsageAnalyticsPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { ClientDetailPage } from '@/features/clients/ClientDetailPage';
import { ClientOnboardingPage } from '@/features/clients/ClientOnboardingPage';
import { VerificationsPage } from '@/features/verifications/VerificationsPage';
import { MyAccountPage } from '@/features/my-account/MyAccountPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { SecurityPage } from '@/features/security/SecurityPage';
import { ApiKeysPage } from '@/features/settings/ApiKeysPage';
import { ComingSoonPage } from '@/features/ComingSoonPage';
import { NotFoundPage } from '@/features/NotFoundPage';
import { ClientOverviewPage } from '@/features/client-portal/ClientOverviewPage';
import { ClientVerifyRequestsPage } from '@/features/client-portal/ClientVerifyRequestsPage';
import { ClientOpportunityRequestsPage } from '@/features/client-portal/ClientOpportunityRequestsPage';
import { ClientUsageAnalyticsPage } from '@/features/client-portal/ClientUsageAnalyticsPage';
import { ClientSubscriptionPage } from '@/features/client-portal/ClientSubscriptionPage';
import { ClientSupportPage } from '@/features/client-portal/ClientSupportPage';
import { ClientTeamPage } from '@/features/client-portal/ClientTeamPage';
import { ClientProductsPage } from '@/features/client-portal/ClientProductsPage';
import { ClientFraudProtectionPage } from '@/features/client-portal/ClientFraudProtectionPage';
import { ClientTrustScoresPage } from '@/features/client-portal/ClientTrustScoresPage';
import { ClientDetectPage } from '@/features/client-portal/ClientDetectPage';
import { ClientRiskEventsPage } from '@/features/client-portal/ClientRiskEventsPage';
import { ClientDeveloperDocsPage } from '@/features/client-portal/ClientDeveloperDocsPage';
import { ClientRequestsPage } from '@/features/client-requests/ClientRequestsPage';
import { AdminClientRequestsPage } from '@/features/client-requests/AdminClientRequestsPage';
import { AdminOpportunityPage } from '@/features/opportunities/AdminOpportunityPage';
import { AdminProductsPage } from '@/features/products/AdminProductsPage';
import { AdminRiskEventsPage } from '@/features/risk/AdminRiskEventsPage';
import { ClientRecoveryCenterPage } from '@/features/client-portal/ClientRecoveryCenterPage';
import { ClientConnectPage } from '@/features/client-portal/ClientConnectPage';
import { ClientPortalGuard } from '@/features/client-portal/ClientPortalGuard';
import { AdminRecoveryIntelligencePage } from '@/features/intelligence/AdminRecoveryIntelligencePage';
import { AdminConnectPage } from '@/features/intelligence/AdminConnectPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { UserSecurityPage } from '@/features/profile/UserSecurityPage';
import type { DashboardRole } from '@/types/auth';

const ALL_CLIENT_PORTAL_ROLES: DashboardRole[] = [
  'AUTHMATECH_CLIENT_ADMIN',
  'AUTHMATECH_CLIENT_OPERATOR',
  'AUTHMATECH_CLIENT_FINANCE',
  'AUTHMATECH_CLIENT_VIEWER',
];

const ALL_AUTHENTICATED_ROLES: DashboardRole[] = [
  ...ALL_CLIENT_PORTAL_ROLES,
  'AUTHMATECH_SUPER_ADMIN',
  'AUTHMATECH_ADMIN',
  'AUTHMATECH_OPERATOR',
  'AUTHMATECH_SUPPORT',
  'AUTHMATECH_FINANCE',
  'AUTHMATECH_VIEWER',
];

/**
 * Wraps a product-specific portal page with a product access check.
 * CLIENT users without the product see the premium locked product experience.
 * Admin users (non-client) pass through unchanged so they can preview any page.
 */
function ProductGatedPage({ productCode, children }: { productCode: string; children: ReactNode }) {
  const { hasAnyRole } = useAuth();
  const isClientUser = hasAnyRole(ALL_CLIENT_PORTAL_ROLES);
  const { getStatus, isLoading } = useClientProductAccess();

  if (!isClientUser) return <>{children}</>;
  if (isLoading) return null;
  if (getStatus(productCode) !== 'ACTIVE') return <LockedProductExperience productCode={productCode} />;
  return <>{children}</>;
}

function RootRedirect() {
  const { hasAnyRole } = useAuth();
  if (hasAnyRole(ALL_CLIENT_PORTAL_ROLES)) {
    return <Navigate to="/portal/overview" replace />;
  }
  return <Navigate to="/overview" replace />;
}

const ADMIN_ROLES: DashboardRole[] = ['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN'];
const OPERATOR_ROLES: DashboardRole[] = [...ADMIN_ROLES, 'AUTHMATECH_OPERATOR'];
const CLIENT_VIEW_ROLES: DashboardRole[] = [...OPERATOR_ROLES, 'AUTHMATECH_SUPPORT'];
const FINANCE_ROLES: DashboardRole[] = [...ADMIN_ROLES, 'AUTHMATECH_FINANCE'];
const REPORT_ROLES: DashboardRole[] = [...FINANCE_ROLES, 'AUTHMATECH_VIEWER'];
const CLIENT_PORTAL_ROLES = ALL_CLIENT_PORTAL_ROLES;

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RootRedirect />} />

        <Route
          path="/overview"
          element={
            <ProtectedRoute requiredRoles={[...REPORT_ROLES, 'AUTHMATECH_OPERATOR', 'AUTHMATECH_SUPPORT']}>
              <OverviewPage />
            </ProtectedRoute>
          }
        />

        {/* ── Internal management — not accessible to AUTHMATECH_CLIENT ── */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute requiredRoles={CLIENT_VIEW_ROLES}>
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/new"
          element={
            <ProtectedRoute requiredRoles={OPERATOR_ROLES}>
              <ClientOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <ProtectedRoute requiredRoles={CLIENT_VIEW_ROLES}>
              <ClientDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/verifications"
          element={
            <ProtectedRoute requiredRoles={CLIENT_VIEW_ROLES}>
              <VerificationsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/opportunities"
          element={
            <ProtectedRoute requiredRoles={CLIENT_VIEW_ROLES}>
              <AdminOpportunityPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRoles={REPORT_ROLES}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        {/* ── Client self-service portal (AUTHMATECH_CLIENT) ── */}
        <Route
          path="/my-verifications"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <VerificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-account"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <MyAccountPage />
            </ProtectedRoute>
          }
        />

        {/* ── Client portal workspace (/portal/**) ── */}
        <Route
          path="/portal/overview"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/verifications"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientVerifyRequestsPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/target"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ProductGatedPage productCode="SIGNAL">
                  <ClientOpportunityRequestsPage />
                </ProductGatedPage>
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        {/* Backward-compatible redirect */}
        <Route path="/portal/opportunities" element={<Navigate to="/portal/target" replace />} />
        <Route
          path="/portal/usage"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientUsageAnalyticsPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/subscription"
          element={
            <ProtectedRoute requiredRoles={['AUTHMATECH_CLIENT_ADMIN', 'AUTHMATECH_CLIENT_FINANCE']}>
              <ClientPortalGuard>
                <ClientSubscriptionPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/products"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientProductsPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/fraud"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ProductGatedPage productCode="SHIELD">
                  <ClientFraudProtectionPage />
                </ProductGatedPage>
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/trust"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ProductGatedPage productCode="TRUST">
                  <ClientTrustScoresPage />
                </ProductGatedPage>
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/detect"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ProductGatedPage productCode="DETECT">
                  <ClientDetectPage />
                </ProductGatedPage>
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        {/* Combined cross-product risk log — available to any client portal user (not Detect-gated). */}
        <Route
          path="/portal/risk-events"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientRiskEventsPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/team"
          element={
            <ProtectedRoute requiredRoles={['AUTHMATECH_CLIENT_ADMIN']}>
              <ClientPortalGuard>
                <ClientTeamPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/docs"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientDeveloperDocsPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/support"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientSupportPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/requests"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ClientRequestsPage />
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/portal/recovery"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ProductGatedPage productCode="STUCK_PLUS">
                  <ClientRecoveryCenterPage />
                </ProductGatedPage>
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/connect"
          element={
            <ProtectedRoute requiredRoles={CLIENT_PORTAL_ROLES}>
              <ClientPortalGuard>
                <ProductGatedPage productCode="CONNECT">
                  <ClientConnectPage />
                </ProductGatedPage>
              </ClientPortalGuard>
            </ProtectedRoute>
          }
        />

        {/* ── Admin: client requests management ── */}
        <Route
          path="/client-requests"
          element={
            <ProtectedRoute requiredRoles={[...ADMIN_ROLES, 'AUTHMATECH_SUPPORT', 'AUTHMATECH_FINANCE']}>
              <AdminClientRequestsPage />
            </ProtectedRoute>
          }
        />

        {/* ── Admin: products + risk intelligence ── */}
        <Route
          path="/products"
          element={
            <ProtectedRoute requiredRoles={ADMIN_ROLES}>
              <AdminProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/risk-events"
          element={
            <ProtectedRoute requiredRoles={[...ADMIN_ROLES, 'AUTHMATECH_OPERATOR', 'AUTHMATECH_SUPPORT']}>
              <AdminRiskEventsPage />
            </ProtectedRoute>
          }
        />

        {/* ── Live usage — hidden from sidebar, route preserved ── */}
        <Route
          path="/live-usage"
          element={
            <ComingSoonPage
              title="Live Usage"
              description="Real-time verification activity stream with auto-refresh and advanced filtering. Coming soon."
              icon={Activity}
            />
          }
        />

        {/* ── Buy Credits — no payment integration yet ── */}
        <Route
          path="/credits"
          element={
            <ProtectedRoute requiredRoles={FINANCE_ROLES}>
              <ComingSoonPage
                title="Buy Credits"
                description="Self-service credit top-up for Verify N and Verify N+ verification quotas. Contact Authmatech to purchase credits."
                icon={CreditCard}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute requiredRoles={REPORT_ROLES}>
              <UsageAnalyticsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/billing"
          element={
            <ProtectedRoute requiredRoles={FINANCE_ROLES}>
              <BillingPage />
            </ProtectedRoute>
          }
        />

        {/* ── Campaign Stats — Opportunity flow is a backend stub ── */}
        <Route
          path="/marketing/campaigns"
          element={
            <ComingSoonPage
              title="Campaign Stats"
              description="Silent visitor identification and retargeting analytics. This feature requires the Authmatech Marketing add-on."
              icon={Megaphone}
            />
          }
        />

        <Route
          path="/settings/api-keys"
          element={
            <ProtectedRoute requiredRoles={ADMIN_ROLES}>
              <ApiKeysPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/security"
          element={
            <ProtectedRoute requiredRoles={ADMIN_ROLES}>
              <SecurityPage />
            </ProtectedRoute>
          }
        />

        {/* ── Admin Intelligence ─────────────────────────────────────── */}
        <Route
          path="/recovery-intelligence"
          element={
            <ProtectedRoute requiredRoles={OPERATOR_ROLES}>
              <AdminRecoveryIntelligencePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connect-intelligence"
          element={
            <ProtectedRoute requiredRoles={OPERATOR_ROLES}>
              <AdminConnectPage />
            </ProtectedRoute>
          }
        />

        {/* ── User Profile & Security ────────────────────────────────── */}
        <Route
          path="/my-profile"
          element={
            <ProtectedRoute requiredRoles={ALL_AUTHENTICATED_ROLES}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-security"
          element={
            <ProtectedRoute requiredRoles={ALL_AUTHENTICATED_ROLES}>
              <UserSecurityPage />
            </ProtectedRoute>
          }
        />

        {/* ── Friendly URL aliases (redirects to canonical routes) ── */}
        <Route path="/portal/fraud-protection" element={<Navigate to="/portal/fraud" replace />} />
        <Route path="/portal/trust-scores" element={<Navigate to="/portal/trust" replace />} />
        <Route path="/portal/developer-docs" element={<Navigate to="/portal/docs" replace />} />
        <Route path="/portal/account" element={<Navigate to="/my-account" replace />} />
        <Route path="/portal/recovery-center" element={<Navigate to="/portal/recovery" replace />} />
        <Route path="/portal/stuck-plus" element={<Navigate to="/portal/recovery" replace />} />
        <Route path="/profile" element={<Navigate to="/my-profile" replace />} />
        <Route path="/portal/profile" element={<Navigate to="/my-profile" replace />} />
        <Route path="/portal/security" element={<Navigate to="/my-security" replace />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
